import { ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { formatFileSize } from './attachmentRules';
import { attachmentTypeLabel } from './attachmentDisplay';
import { AttachmentTypeIcon } from './AttachmentTypeIcon';
import { stagedAttachmentName, type StagedAttachment } from './stagedAttachment';
import { useObjectUrl } from './useObjectUrl';

interface StagedAttachmentChipProps {
    staged: StagedAttachment;
    onRemove: () => void;
}

function describeStaged(staged: StagedAttachment): string {
    const type = attachmentTypeLabel(staged.mimeType);
    if (staged.source === 'quick-reply') return `Quick reply media · ${type}`;
    return `${type} · ${formatFileSize(staged.size)}`;
}

function ChipThumbnail({ staged }: { staged: StagedAttachment }) {
    const isImageFile = staged.source === 'file' && staged.mimeType.startsWith('image/');
    const previewUrl = useObjectUrl(isImageFile ? staged.file : null);

    if (previewUrl) {
        return (
            <img
                src={previewUrl}
                alt=""
                className="w-10 h-10 flex-shrink-0 rounded-md object-cover bg-gray-200"
            />
        );
    }
    return (
        <span className="w-10 h-10 flex-shrink-0 rounded-md bg-white border border-gray-200 flex items-center justify-center text-gray-500">
            <AttachmentTypeIcon mimeType={staged.mimeType} className="w-5 h-5" />
        </span>
    );
}

function StagedAttachmentChip({ staged, onRemove }: StagedAttachmentChipProps) {
    const name = stagedAttachmentName(staged);
    return (
        <div className="flex flex-col gap-1 min-w-0">
            <div
                role="group"
                aria-label={`Attachment: ${name}`}
                className="flex items-center gap-2 min-w-0 max-w-sm rounded-lg border border-gray-200 bg-gray-50 p-1.5"
            >
                <ChipThumbnail staged={staged} />
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-800 truncate" title={name}>
                        {name}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{describeStaged(staged)}</p>
                </div>
                <button
                    type="button"
                    onClick={onRemove}
                    aria-label={`Remove attachment ${name}`}
                    className="w-7 h-7 flex-shrink-0 rounded-md flex items-center justify-center text-gray-400 hover:bg-gray-200 hover:text-gray-700"
                >
                    <XMarkIcon className="w-4 h-4" />
                </button>
            </div>
            {staged.replaced && (
                <p role="status" className="flex items-center gap-1 text-xs text-gray-500 min-w-0">
                    <ArrowPathIcon className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
                    <span className="truncate">Replaced &ldquo;{staged.replaced}&rdquo;</span>
                </p>
            )}
        </div>
    );
}

export { StagedAttachmentChip };
